import { defineComponent as _defineComponent } from 'vue';
import { BCard } from "bootstrap-vue";
import * as d3 from "d3";
import { computed, nextTick, onMounted, ref, watch } from "vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'BarChart',
    props: {
        data: null,
        title: { default: "Bar Chart" },
        description: { default: undefined },
        width: { default: 720 },
        height: { default: 400 },
        enableTooltips: { type: Boolean, default: true },
        enableSelection: { type: Boolean, default: false },
        labelFormatter: { type: Function, default: (dataPoint) => dataPoint ? `${dataPoint.label}: ${dataPoint.value}` : "No data" },
        valueFormatter: { type: Function, default: (value) => `${value}` }
    },
    emits: ["show-tooltip", "hide-tooltip", "selection-changed"],
    setup(__props, { emit }) {
        const props = __props;
        const barChart = ref(null);
        const legend = ref(null);
        const chartTooltip = ref(null);
        const tooltipDataPoint = ref(null);
        const selectedDataPoint = ref(undefined);
        const chartBars = ref(null);
        const legendEntries = ref(null);
        const showTooltip = computed(() => props.enableTooltips && tooltipDataPoint.value !== null);
        const hasData = computed(() => props.data.length > 0 && props.data.reduce((total, dataPoint) => total + dataPoint.value, 0) > 0);
        onMounted(() => {
            renderBarChart();
        });
        watch(() => [
            props.data,
            props.width,
            props.height,
            props.enableTooltips,
            props.enableSelection,
            props.labelFormatter,
            props.valueFormatter,
        ], () => {
            // make sure v-if to conditionally display the div we're rendering this in
            // is available in the DOM before actually doing the rendering. Without
            // nextTick you cannot go from empty data -> chart when tweaking filtering
            // parameters.
            nextTick(() => {
                clearChart();
                renderBarChart();
            });
        });
        function renderBarChart() {
            chartBars.value = drawChart();
            legendEntries.value = createLegend();
            setupEvents();
        }
        function clearChart() {
            d3.select(barChart.value).selectAll("*").remove();
            d3.select(legend.value).selectAll("*").remove();
        }
        const color = d3.scaleOrdinal(d3.schemeCategory10);
        function drawChart() {
            const yAxisWidth = 70;
            const xAxisHeight = 5;
            const data = props.data;
            const chartWidth = props.width - yAxisWidth;
            const chartHeight = props.height;
            const chartXStart = yAxisWidth;
            const tickFontSize = 14;
            const svg = d3.select(barChart.value).append("svg").attr("width", props.width).attr("height", props.height);
            const g = svg.append("g").attr("transform", `translate(${chartXStart}, 0)`);
            const xScale = d3
                .scaleBand()
                .range([0, chartWidth - chartXStart])
                .domain(data.map(function (d) {
                return d.id;
            }))
                .padding(0.1);
            const xAxis = d3
                .axisBottom(xScale)
                .tickSize(0)
                .tickFormat(() => "");
            const yScale = d3
                .scaleLinear()
                .range([chartHeight - xAxisHeight, 0])
                .domain([0, d3.max(data, (d) => d.value) || 0]);
            const yAxis = d3
                .axisLeft(yScale)
                .scale(yScale)
                .tickFormat(function (d) {
                return props.valueFormatter(d);
            })
                .ticks(5)
                .tickSizeInner(5)
                .tickSizeOuter(0);
            g.append("g")
                .attr("class", "axis")
                .attr("transform", "translate(0," + (chartHeight - xAxisHeight) + ")")
                .call(xAxis);
            g.append("g").attr("class", "axis").call(yAxis);
            g.selectAll(".tick text").style("font-size", `${tickFontSize}px`);
            const bars = g
                .selectAll(".bar")
                .data(data)
                .enter()
                .append("rect")
                .classed("bar", true)
                .attr("x", (d) => xScale(d.id) || 0)
                .attr("y", (d) => yScale(d.value) - xAxisHeight)
                .attr("width", xScale.bandwidth())
                .attr("height", (d) => chartHeight - yScale(d.value))
                .attr("fill", (d) => entryColor(d));
            return bars;
        }
        function createLegend() {
            var _a;
            const data = props.data;
            const topMargin = 10;
            const entrySpacing = 18;
            const entryRadius = 5;
            const labelOffset = 4;
            const baselineOffset = 4;
            const height = topMargin + data.length * entrySpacing;
            const container = d3
                .select(legend.value)
                .append("svg")
                .attr("height", height)
                .append("g")
                .attr("transform", `translate(0,${topMargin})`);
            const entries = container
                .selectAll("g")
                .data(data)
                .join("g")
                .classed("legend-item", true)
                .attr("transform", (d, i) => `translate(0,${topMargin + i * entrySpacing})`);
            entries
                .append("circle")
                .attr("cx", entryRadius)
                .attr("r", entryRadius)
                .attr("fill", (d) => entryColor(d));
            entries
                .append("text")
                .attr("x", 2 * entryRadius + labelOffset)
                .attr("y", baselineOffset)
                .attr("fill", "black")
                .text((d) => props.labelFormatter(d));
            // Set the width of the SVG to the width of the widest entry
            let maxWidth = 0;
            for (const node of entries.nodes()) {
                const width = node.getBoundingClientRect().width;
                maxWidth = Math.max(maxWidth, width);
            }
            const svg = (_a = container.node()) === null || _a === void 0 ? void 0 : _a.closest("svg");
            if (svg) {
                svg.setAttribute("width", `${maxWidth}`);
            }
            return entries;
        }
        function setupEvents() {
            if (props.enableTooltips) {
                setupTooltipEvents();
            }
            if (props.enableSelection) {
                setupSelectionEvents();
            }
        }
        function setupTooltipEvents() {
            var _a;
            (_a = chartBars.value) === null || _a === void 0 ? void 0 : _a.on("mouseenter", (event, d) => {
                tooltipDataPoint.value = d;
                emit("show-tooltip", d, event.pageX, event.pageY);
            }).on("mousemove", (event) => {
                // Correct the page coordinates to compensate for scrolling offset
                const correctedX = event.pageX - window.scrollX;
                const correctedY = event.pageY - window.scrollY;
                setTooltipPosition(correctedX, correctedY);
            }).on("mouseleave", () => {
                tooltipDataPoint.value = null;
                emit("hide-tooltip");
            });
        }
        function setupSelectionEvents() {
            var _a, _b, _c, _d;
            (_a = chartBars.value) === null || _a === void 0 ? void 0 : _a.classed("clickable", true);
            (_b = chartBars.value) === null || _b === void 0 ? void 0 : _b.on("click", (event, d) => {
                event.stopPropagation();
                toggleSelection(d);
            });
            (_c = legendEntries.value) === null || _c === void 0 ? void 0 : _c.classed("clickable", true);
            (_d = legendEntries.value) === null || _d === void 0 ? void 0 : _d.on("click", (event, d) => {
                event.stopPropagation();
                toggleSelection(d);
            });
            // Cancel selection on click anywhere else
            d3.select("body").on("click", () => {
                if (selectedDataPoint.value) {
                    toggleSelection(selectedDataPoint.value);
                }
            });
        }
        function toggleSelection(dataPoint) {
            if (selectedDataPoint.value === dataPoint) {
                selectedDataPoint.value = undefined;
            }
            else {
                selectedDataPoint.value = dataPoint;
            }
            highlightSelected();
            emit("selection-changed", selectedDataPoint.value);
        }
        function highlightSelected() {
            var _a;
            if (!chartBars.value) {
                return;
            }
            chartBars.value
                .transition()
                .duration(200)
                .attr("opacity", (d) => (selectedDataPoint.value === undefined || d === selectedDataPoint.value ? 1.0 : 0.5));
            (_a = legendEntries.value) === null || _a === void 0 ? void 0 : _a.selectAll("*").transition().duration(200).attr("opacity", (d) => (selectedDataPoint.value === undefined || d === selectedDataPoint.value ? 1.0 : 0.5));
        }
        function entryColor(d) {
            return color(`${d.id}`);
        }
        const tooltipMargin = 40;
        function setTooltipPosition(mouseX, mouseY) {
            const tooltip = chartTooltip.value;
            if (tooltip) {
                let x = mouseX;
                let y = mouseY;
                const width = tooltip.offsetWidth;
                const height = tooltip.offsetHeight;
                if (x + width + tooltipMargin > window.innerWidth) {
                    x = window.innerWidth - width - tooltipMargin;
                }
                if (y + height + tooltipMargin > window.innerHeight) {
                    y = window.innerHeight - height - tooltipMargin;
                }
                tooltip.style.left = `${x}px`;
                tooltip.style.top = `${y}px`;
            }
        }
        return { __sfc: true, props, emit, barChart, legend, chartTooltip, tooltipDataPoint, selectedDataPoint, chartBars, legendEntries, showTooltip, hasData, renderBarChart, clearChart, color, drawChart, createLegend, setupEvents, setupTooltipEvents, setupSelectionEvents, toggleSelection, highlightSelected, entryColor, tooltipMargin, setTooltipPosition, BCard };
    }
});
